import React from "react";
import { PushPin } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import styles from "../CaseActivityCard.module.css";
import { getActivityTimestampDisplay } from "../parts/scheduledEmailTimestamp";

/**
 * CardShell
 *
 * Provides the outer card border/animation wrapper, the priority pin, and the
 * four-column flex layout (left · middle · right-content · right-actions).
 *
 * Props:
 *   activity         – full activity object
 *   animateExpand    – boolean that switches between rootAnimation / root CSS class (default: false)
 *   dottedborder     – when defined, applies the dashed border modifier
 *   icon             – ReactNode rendered in the left icon slot
 *   typeText         – ReactNode rendered as the bold "type" label
 *   typeSubText      – ReactNode rendered as the smaller sub-label (user / "automated")
 *   contentPreview   – ReactNode rendered in the clickable right-content column
 *   openerColumn     – ReactNode rendered in the far-right actions column
 *   onContentClick   – click handler forwarded to the content column (default: undefined)
 *   hasOpener        – boolean; switches the content column between opener and static layout styles (default: false)
 *   children         – expanded detail area rendered below the flex row
 */
export default function CardShell({
  activity,
  animateExpand = false,
  dottedborder,
  icon,
  typeText,
  typeSubText,
  contentPreview = null,
  openerColumn = null,
  onContentClick,
  hasOpener = false,
  children,
}) {
  const isPriority =
    activity.priority === "HIGH" || activity.priority === "LOW";

  const rootClass = [
    animateExpand ? styles.rootAnimation : styles.root,
    isPriority ? styles.priority : "",
    dottedborder !== undefined ? styles.root_dotted_border : "",
  ]
    .filter(Boolean)
    .join(" ");

  const { label: timestamp, tooltip: timestampTooltip } =
    getActivityTimestampDisplay(activity);

  const timestampEl = (
    <div className={styles.left_timestamp} data-testid="activity-timestamp">
      {timestamp}
    </div>
  );

  return (
    <div>
      <div className={rootClass}>
        {isPriority && (
          <Tooltip title="Priority activity">
            <PushPin
              className={styles.priority_pin}
              data-testid="priority-indicator"
            />
          </Tooltip>
        )}

        <div className={styles.rootFlex}>
          {/* ── Left column: icon + timestamp ── */}
          <div className={styles.left_container}>
            <div className={styles.left_icon}>{icon}</div>
            {timestampTooltip ? (
              <Tooltip
                title={timestampTooltip}
                enterDelay={0}
                slotProps={{
                  tooltip: { "data-testid": "activity-timestamp-tooltip" },
                }}
              >
                {timestampEl}
              </Tooltip>
            ) : (
              timestampEl
            )}
          </div>

          {/* ── Middle column: type label + user/sub-label ── */}
          <div className={styles.middle_container}>
            <div className={styles.middle_type}>{typeText}</div>
            <div className={styles.middle_user}>{typeSubText}</div>
          </div>

          {/* ── Right content column (clickable when expandable) ── */}
          <div
            className={
              hasOpener ? styles.right_container_opener : styles.right_container
            }
            onClick={onContentClick}
          >
            {contentPreview}
          </div>

          {/* ── Far-right actions column: delete / menu / expand ── */}
          <div
            className={`${styles.right_opener} ${
              !hasOpener ? styles.right_opener_top : ""
            }`}
          >
            {openerColumn}
          </div>
        </div>

        {/* ── Expanded detail area (rendered below the row) ── */}
        {children}
      </div>
    </div>
  );
}
